/**
 * Embed mode — Arco y Flecha running inside someone else's page.
 *
 * losdelcamino.net/las-escrituras/ (Koresh's WordPress scripture reader) embeds
 * the chat in an iframe with the question pre-filled:
 *   <iframe src="https://chat.losdelcamino.net/chat?q=…&lang=es&send=hold">
 *
 * In that context the app's own chrome is wrong: the topbar collides with the
 * composer (so the pre-filled question is not even visible), the nav offers
 * routes that would strand the reader inside a ~560px frame, and the home
 * footer links back to the very page hosting the iframe. Embed mode strips the
 * app down to the one thing the host page wants: ask, and read the answer.
 *
 * Detection, first match wins:
 *   1. `?embed=1` / `?embed=0` — explicit, and the escape hatch. Lets the host
 *      opt in without relying on frame sniffing, and lets us open a normal
 *      full app inside a frame when we want one.
 *   2. Being framed at all (`window.self !== window.top`) — so the current
 *      embed benefits with NO change to Koresh's plugin.
 *
 * This cannot change during a session, so callers may treat it as a constant.
 */

/** Pure decision, so it can be smoke-tested without a DOM. */
export function resolveEmbedMode(search: string, isFramed: boolean): boolean {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search);
  } catch {
    return isFramed;
  }
  const explicit = params.get("embed");
  if (explicit === "1" || explicit === "true") return true;
  if (explicit === "0" || explicit === "false") return false;
  return isFramed;
}

/** True when this document is nested in another browsing context. */
export function isFramed(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin ancestors can make the comparison throw in some engines;
    // if we cannot tell, we are almost certainly framed.
    return true;
  }
}

/** Runtime answer for this page load. Constant for the session. */
export function detectEmbedMode(): boolean {
  if (typeof window === "undefined") return false;
  return resolveEmbedMode(window.location.search, isFramed());
}

let cached: boolean | null = null;

/**
 * Memoized accessor. Deliberately NOT a hook and NOT prop-drilled: this is an
 * environment fact fixed at page load, and the components that need it (the
 * source cards, five levels down) would otherwise thread a prop through every
 * layer between. Resolved on first call, then constant.
 */
export function isEmbedded(): boolean {
  if (cached === null) cached = detectEmbedMode();
  return cached;
}

/** Test seam — resets the memo so smokes can exercise both modes. */
export function resetEmbedModeForTests(value: boolean | null = null): void {
  cached = value;
}
